import { useNavigate, useSearchParams } from "react-router-dom";
import { authErrorMessage, useAuth } from "@/auth";
import { isHidden } from "@/lib/hidden-features";
import { ToastHost, useToast } from "@/utility/toast-utility";
import {
  Anchor,
  Box,
  Button,
  Center,
  Divider,
  Group,
  PasswordInput,
  Text,
  TextInput,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { AppleLogoIcon } from "@phosphor-icons/react/dist/ssr";
import { useFormik } from "formik";
import { useState } from "react";
import * as Yup from "yup";

type LoginForm = { email: string; password: string };

const GoogleMark = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const { closeToast, showToast, toast } = useToast();
  const { colorScheme } = useMantineColorScheme();
  const [visible, toggle] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  // ProtectedRoute sends unauthenticated visitors here with ?redirect=<where they were>.
  const redirectTo = searchParams.get("redirect") || "/app/home";
  const afterSignIn = () => navigate(auth.emailVerified ? redirectTo : "/auth/verify-email", { replace: true });

  const formik = useFormik<LoginForm>({
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string().email("Email address must be a valid email").required("Email is required").label("Email"),
      password: Yup.string().required("Password is required").label("Password"),
    }),
    onSubmit: async (values) => {
      try {
        await auth.signIn(values.email.trim(), values.password);
        afterSignIn();
      } catch (err) {
        showToast({ type: "error", message: authErrorMessage(err) });
      }
    },
  });

  const signInWithGoogle = async () => {
    setGoogleBusy(true);
    try {
      await auth.signInWithGoogle();
      afterSignIn();
    } catch (err) {
      showToast({ type: "error", message: authErrorMessage(err) });
    } finally {
      setGoogleBusy(false);
    }
  };

  const socialButton = (onClick: () => void, children: React.ReactNode, label: string) => (
    <Box
      component="button"
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={googleBusy}
      style={{
        width: 40,
        height: 40,
        border: "none",
        background: "transparent",
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      {children}
    </Box>
  );

  return (
    <Box>
      <ToastHost toast={toast} onClose={closeToast} />
      <Title ta="center" order={3} mb="md">
        Sign In
      </Title>
      <form onSubmit={formik.handleSubmit}>
        <TextInput
          label="Email"
          placeholder="Enter Email"
          size="md"
          withAsterisk
          name="email"
          type="email"
          autoComplete="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && formik.errors.email}
        />
        <PasswordInput
          mt="md"
          label="Password"
          placeholder="Enter Password"
          size="md"
          withAsterisk
          name="password"
          autoComplete="current-password"
          visible={visible}
          onVisibilityChange={toggle}
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && formik.errors.password}
        />

        <Group justify="flex-end" mt="md" wrap="wrap">
          <Anchor size="md" onClick={() => navigate("/auth/forgot-password")}>
            Forgot Password?
          </Anchor>
        </Group>

        <Button type="submit" size="md" mt="md" fullWidth loading={formik.isSubmitting}>
          Sign In
        </Button>

        <Divider mt="lg" my="xs" label={<Text fw={500} size="md">Continue with</Text>} labelPosition="center" />

        <Center mt="md">
          <Group gap="md">
            {!isHidden("appleSignIn") &&
              socialButton(
                () => showToast({ type: "error", message: "Apple sign-in is not available yet." }),
                <AppleLogoIcon weight="fill" size={24} color={colorScheme === "dark" ? "white" : "black"} />,
                "Continue with Apple",
              )}
            {socialButton(signInWithGoogle, <GoogleMark />, "Continue with Google")}
          </Group>
        </Center>

        <Text ta="center" mt="md">
          Don't have an account ?{" "}
          <Anchor fw={500} onClick={() => navigate("/auth/sign-up")}>
            Sign Up
          </Anchor>
        </Text>
      </form>
    </Box>
  );
}
